import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Shield, Edit2, Plus, RefreshCw, History, X, Check, MinusCircle, PlusCircle,
  AlertTriangle, ChevronDown, ChevronUp, Trash2,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return s; }
};

const DepositCard = ({ tenantId, token, onChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/tenants/${tenantId}/deposit-summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch (e) {
      toast({
        title: 'Could not load deposit',
        description: e.response?.data?.detail || '',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [tenantId, token]);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    await load();
    onChanged && onChanged();
  };

  if (loading || !data) {
    return (
      <div className="p-4 bg-blue-50/50 rounded-xl border border-blue-100" data-testid="deposit-card-loading">
        <div className="flex items-center gap-2 text-blue-700">
          <Shield size={18} /> <span className="text-sm">Loading deposit…</span>
        </div>
      </div>
    );
  }

  const { expected, collected, pending, overpaid, refunded, deducted, status, payments, refunds, audit } = data;
  const pct = expected > 0 ? Math.min(100, Math.round((collected / expected) * 100)) : 0;

  const statusBadge = {
    not_set:     { cls: 'bg-gray-100 text-gray-700',   label: 'Not set' },
    unpaid:      { cls: 'bg-red-100 text-red-700',     label: 'Unpaid' },
    partial:     { cls: 'bg-amber-100 text-amber-700', label: `${pct}% collected` },
    fully_paid:  { cls: 'bg-green-100 text-green-700', label: 'Fully paid' },
    overpaid:    { cls: 'bg-purple-100 text-purple-700', label: `Overpaid by ${inr(overpaid)}` },
  }[status] || { cls: 'bg-gray-100 text-gray-700', label: status };

  return (
    <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50/40 rounded-xl border border-blue-100" data-testid="deposit-card">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-blue-600/10 text-blue-700 flex items-center justify-center">
            <Shield size={18} />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider font-semibold text-blue-800/80">Security Deposit</p>
            <p className="text-2xl font-bold text-blue-900 tabular-nums" data-testid="deposit-expected">
              {inr(expected)}
              <span className="text-xs font-medium text-blue-700/60 ml-1.5">expected</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-wrap justify-end">
          <span data-testid="deposit-status-badge" className={`text-[10px] font-semibold px-2 py-1 rounded-full ${statusBadge.cls}`}>
            {statusBadge.label}
          </span>
          <Button size="sm" variant="outline" onClick={() => setEditOpen(true)} className="h-7 px-2 text-xs" data-testid="edit-deposit-btn">
            <Edit2 size={12} className="mr-1" /> Edit
          </Button>
        </div>
      </div>

      {/* Progress bar */}
      {expected > 0 && (
        <div className="mb-3" data-testid="deposit-progress">
          <div className="h-2 bg-blue-100 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-500 ${status === 'overpaid' ? 'bg-purple-500' : status === 'fully_paid' ? 'bg-green-500' : 'bg-blue-500'}`}
              style={{ width: `${Math.min(100, (collected / expected) * 100)}%` }}
            />
          </div>
          <div className="flex justify-between text-[11px] text-blue-900/70 mt-1 font-medium">
            <span data-testid="deposit-collected">Collected {inr(collected)}</span>
            {pending > 0
              ? <span className="text-amber-700">Pending {inr(pending)}</span>
              : overpaid > 0
                ? <span className="text-purple-700">Overpaid {inr(overpaid)}</span>
                : <span className="text-green-700">Settled</span>}
          </div>
        </div>
      )}

      {/* Refunds / deductions strip (only when any exist) */}
      {(refunded > 0 || deducted > 0) && (
        <div className="grid grid-cols-2 gap-2 mb-3 text-xs" data-testid="deposit-refund-strip">
          <div className="bg-white/60 rounded-lg p-2 flex items-center gap-1.5">
            <MinusCircle size={14} className="text-rose-500" />
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">Deducted</p>
              <p className="font-semibold text-rose-700">{inr(deducted)}</p>
            </div>
          </div>
          <div className="bg-white/60 rounded-lg p-2 flex items-center gap-1.5">
            <PlusCircle size={14} className="text-emerald-500" />
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">Refunded</p>
              <p className="font-semibold text-emerald-700">{inr(refunded)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 flex-wrap">
        <Button
          size="sm"
          onClick={() => setPayOpen(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-8"
          data-testid="record-partial-deposit-btn"
        >
          <Plus size={12} className="mr-1" /> Record partial deposit
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setRefundOpen(true)}
          className="text-xs h-8"
          data-testid="record-refund-btn"
        >
          <RefreshCw size={12} className="mr-1" /> Refund / deduction
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setHistoryOpen(v => !v)}
          className="text-xs h-8 ml-auto text-blue-700"
          data-testid="toggle-history-btn"
        >
          <History size={12} className="mr-1" /> History ({payments.length + refunds.length + audit.length})
          {historyOpen ? <ChevronUp size={12} className="ml-1" /> : <ChevronDown size={12} className="ml-1" />}
        </Button>
      </div>

      {/* History */}
      {historyOpen && (
        <HistoryPanel
          payments={payments}
          refunds={refunds}
          audit={audit}
          tenantId={tenantId}
          token={token}
          onChanged={refresh}
        />
      )}

      {/* Modals */}
      <EditExpectedModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        current={expected}
        tenantId={tenantId}
        token={token}
        onSaved={refresh}
      />
      <RecordPartialDepositModal
        open={payOpen}
        onClose={() => setPayOpen(false)}
        tenantId={tenantId}
        token={token}
        suggestedAmount={pending > 0 ? pending : expected}
        onSaved={refresh}
      />
      <RecordRefundModal
        open={refundOpen}
        onClose={() => setRefundOpen(false)}
        tenantId={tenantId}
        token={token}
        maxRefund={Math.max(0, collected - deducted - refunded)}
        onSaved={refresh}
      />
    </div>
  );
};

// ==================== Edit Expected Modal ====================
const EditExpectedModal = ({ open, onClose, current, tenantId, token, onSaved }) => {
  const [amount, setAmount] = useState(current || '');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setAmount(current || ''); setReason(''); } }, [open, current]);

  const save = async () => {
    const amt = parseFloat(amount);
    if (Number.isNaN(amt) || amt < 0) {
      toast({ title: 'Enter a valid amount', variant: 'destructive' });
      return;
    }
    if (reason.trim().length < 3) {
      toast({ title: 'Reason is required', description: 'Please write a short note so you remember why it changed.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await axios.patch(
        `${API}/tenants/${tenantId}/deposit`,
        { expected: amt, reason: reason.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: 'Expected deposit updated' });
      onSaved();
      onClose();
    } catch (e) {
      toast({
        title: 'Update failed',
        description: e.response?.data?.detail || 'Could not save',
        variant: 'destructive',
      });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="edit-deposit-modal" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit2 size={16} /> Edit expected deposit
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Expected amount (₹)</Label>
            <Input
              type="number"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 15000"
              data-testid="edit-deposit-amount-input"
            />
          </div>
          <div>
            <Label className="text-xs">
              Reason <span className="text-red-500">*</span>
              <span className="text-gray-400 font-normal ml-1">(audit trail)</span>
            </Label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="e.g. Increased deposit after rent revision / Correction — I told tenant wrong number earlier"
              className="w-full text-sm border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-200"
              data-testid="edit-deposit-reason-input"
              maxLength={300}
            />
            <p className="text-[10px] text-gray-500 mt-0.5">{reason.length}/300</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="edit-deposit-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="edit-deposit-save">
            {saving ? 'Saving…' : (<><Check size={14} className="mr-1" /> Save</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== Record Partial Deposit Modal ====================
const RecordPartialDepositModal = ({ open, onClose, tenantId, token, suggestedAmount, onSaved }) => {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('upi');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [transactionId, setTransactionId] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setAmount(suggestedAmount > 0 ? String(suggestedAmount) : '');
      setMethod('upi');
      setDate(new Date().toISOString().slice(0, 10));
      setTransactionId('');
      setNotes('');
    }
  }, [open, suggestedAmount]);

  const save = async () => {
    const amt = parseFloat(amount);
    if (Number.isNaN(amt) || amt <= 0) {
      toast({ title: 'Enter a valid amount', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      // Fetch tenant to get property/room context expected by /api/money/payments
      const t = await axios.get(`${API}/tenants/${tenantId}`, { headers: { Authorization: `Bearer ${token}` } });
      const tenant = t.data;
      await axios.post(
        `${API}/money/payments`,
        {
          tenant_id: tenantId,
          property_id: tenant.property_id,
          room_id: tenant.room_id,
          amount: amt,
          payment_type: 'deposit',
          payment_method: method,
          payment_date: date,
          month: date.slice(0, 7),
          transaction_id: transactionId || undefined,
          notes: notes || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: 'Partial deposit recorded', description: `${inr(amt)} via ${method.toUpperCase()}` });
      onSaved();
      onClose();
    } catch (e) {
      toast({
        title: 'Could not record',
        description: e.response?.data?.detail || '',
        variant: 'destructive',
      });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="partial-deposit-modal" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus size={16} /> Record partial deposit
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-xs">Amount (₹)</Label>
            <Input
              type="number"
              min="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 5000"
              data-testid="partial-deposit-amount"
            />
          </div>
          <div>
            <Label className="text-xs">Method</Label>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              className="w-full text-sm border border-gray-300 rounded-md p-2 h-9"
              data-testid="partial-deposit-method"
            >
              <option value="upi">UPI</option>
              <option value="cash">Cash</option>
              <option value="bank_transfer">Bank transfer</option>
              <option value="cheque">Cheque</option>
              <option value="card">Card</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="partial-deposit-date" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Transaction ID (optional)</Label>
            <Input value={transactionId} onChange={(e) => setTransactionId(e.target.value)} placeholder="UPI ref / cheque no." />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Notes (optional)</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. 1st installment" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="partial-deposit-save">
            {saving ? 'Saving…' : (<><Check size={14} className="mr-1" /> Record</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== Record Refund / Deduction Modal ====================
const RecordRefundModal = ({ open, onClose, tenantId, token, maxRefund, onSaved }) => {
  const [type, setType] = useState('deduction');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setType('deduction'); setAmount(''); setReason(''); setMethod('');
      setDate(new Date().toISOString().slice(0, 10));
    }
  }, [open]);

  const save = async () => {
    const amt = parseFloat(amount);
    if (Number.isNaN(amt) || amt <= 0) {
      toast({ title: 'Enter a valid amount', variant: 'destructive' });
      return;
    }
    if (reason.trim().length < 3) {
      toast({ title: 'Reason is required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/tenants/${tenantId}/deposit/refund`,
        { type, amount: amt, reason: reason.trim(), date, method: method || undefined },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: `${type === 'refund' ? 'Refund' : 'Deduction'} recorded` });
      onSaved();
      onClose();
    } catch (e) {
      toast({
        title: 'Could not record',
        description: e.response?.data?.detail || '',
        variant: 'destructive',
      });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="refund-modal" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw size={16} /> Deposit refund / deduction
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {/* Type toggle */}
          <div className="flex gap-2" data-testid="refund-type-toggle">
            {[
              { k: 'deduction', label: 'Deduction (I kept this)', tint: 'bg-rose-100 text-rose-700 border-rose-300', cls: 'data-[active=true]:bg-rose-600 data-[active=true]:text-white' },
              { k: 'refund',    label: 'Refund (I paid back)',    tint: 'bg-emerald-100 text-emerald-700 border-emerald-300', cls: 'data-[active=true]:bg-emerald-600 data-[active=true]:text-white' },
            ].map(opt => (
              <button
                key={opt.k}
                data-testid={`refund-type-${opt.k}`}
                data-active={type === opt.k}
                onClick={() => setType(opt.k)}
                className={`flex-1 text-xs font-semibold px-3 py-2 rounded-md border transition-colors ${opt.tint} ${opt.cls}`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Amount (₹)</Label>
              <Input
                type="number"
                min="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={type === 'refund' ? `Max ${inr(maxRefund)}` : 'e.g. 2000'}
                data-testid="refund-amount"
              />
              {type === 'refund' && maxRefund > 0 && (
                <p className="text-[10px] text-gray-500 mt-0.5">Net collected - deducted: {inr(maxRefund)}</p>
              )}
            </div>
            <div>
              <Label className="text-xs">Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Method (optional)</Label>
              <select
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                className="w-full text-sm border border-gray-300 rounded-md p-2 h-9"
              >
                <option value="">—</option>
                <option value="upi">UPI</option>
                <option value="cash">Cash</option>
                <option value="bank_transfer">Bank transfer</option>
                <option value="cheque">Cheque</option>
                <option value="adjustment">Adjustment</option>
              </select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">
                Reason <span className="text-red-500">*</span>
              </Label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                placeholder={type === 'refund' ? 'e.g. Full refund at move-out — room was clean' : 'e.g. Cleaning charges / damage to bed'}
                className="w-full text-sm border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-200"
                data-testid="refund-reason"
                maxLength={300}
              />
            </div>
          </div>

          {type === 'refund' && parseFloat(amount) > maxRefund && maxRefund >= 0 && (
            <div className="flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 rounded-md p-2 text-amber-800">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
              <span>Refund exceeds net collected balance. It will still be recorded — please double-check.</span>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            onClick={save}
            disabled={saving}
            className={type === 'refund' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-rose-600 hover:bg-rose-700 text-white'}
            data-testid="refund-save"
          >
            {saving ? 'Saving…' : (<><Check size={14} className="mr-1" /> Record</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== History Panel ====================
const HistoryPanel = ({ payments, refunds, audit, tenantId, token, onChanged }) => {
  // Unified timeline (newest first)
  const items = [
    ...payments.map(p => ({
      kind: 'payment', at: p.payment_date || p.created_at, id: p.id,
      amount: p.amount, method: p.payment_method, notes: p.notes, txn: p.transaction_id,
    })),
    ...refunds.map(r => ({
      kind: r.type === 'refund' ? 'refund' : 'deduction', at: r.date || r.created_at, id: r.id,
      amount: r.amount, method: r.method, reason: r.reason,
    })),
    ...audit.map(a => ({
      kind: 'edit', at: a.at, id: a.id,
      old: a.old_expected, new: a.new_expected, delta: a.delta, reason: a.reason,
    })),
  ].sort((a, b) => (b.at || '').localeCompare(a.at || ''));

  const removeRefund = async (rid) => {
    if (!window.confirm('Delete this refund/deduction entry?')) return;
    try {
      await axios.delete(`${API}/tenants/${tenantId}/deposit/refund/${rid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: 'Entry removed' });
      onChanged();
    } catch (e) {
      toast({ title: 'Failed', description: e.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  if (items.length === 0) {
    return (
      <div className="mt-3 p-3 bg-white/70 rounded-lg text-center text-xs text-gray-500" data-testid="deposit-history-empty">
        No activity yet. Record the first partial deposit above.
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-1.5 max-h-80 overflow-y-auto" data-testid="deposit-history-list">
      {items.map(it => {
        if (it.kind === 'payment') {
          return (
            <div key={`p-${it.id}`} className="flex items-center gap-2 p-2 bg-white rounded-lg border border-blue-100 text-xs" data-testid="history-row-payment">
              <span className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0">
                <Plus size={12} />
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 truncate">
                  Partial deposit collected
                </p>
                <p className="text-[10px] text-gray-500 truncate">
                  {fmtDate(it.at)} · {(it.method || '').toUpperCase()}{it.txn ? ` · ${it.txn}` : ''}{it.notes ? ` · ${it.notes}` : ''}
                </p>
              </div>
              <span className="font-bold text-blue-700 tabular-nums whitespace-nowrap">+{inr(it.amount)}</span>
            </div>
          );
        }
        if (it.kind === 'deduction') {
          return (
            <div key={`d-${it.id}`} className="flex items-center gap-2 p-2 bg-white rounded-lg border border-rose-100 text-xs" data-testid="history-row-deduction">
              <span className="w-7 h-7 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center flex-shrink-0">
                <MinusCircle size={12} />
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 truncate">Deduction</p>
                <p className="text-[10px] text-gray-500 truncate">{fmtDate(it.at)} · {it.reason}</p>
              </div>
              <span className="font-bold text-rose-700 tabular-nums whitespace-nowrap">−{inr(it.amount)}</span>
              <button onClick={() => removeRefund(it.id)} className="text-gray-300 hover:text-red-500 p-0.5" title="Remove">
                <Trash2 size={12} />
              </button>
            </div>
          );
        }
        if (it.kind === 'refund') {
          return (
            <div key={`r-${it.id}`} className="flex items-center gap-2 p-2 bg-white rounded-lg border border-emerald-100 text-xs" data-testid="history-row-refund">
              <span className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                <PlusCircle size={12} />
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 truncate">Refund paid back to tenant</p>
                <p className="text-[10px] text-gray-500 truncate">{fmtDate(it.at)}{it.method ? ` · ${it.method.toUpperCase()}` : ''} · {it.reason}</p>
              </div>
              <span className="font-bold text-emerald-700 tabular-nums whitespace-nowrap">−{inr(it.amount)}</span>
              <button onClick={() => removeRefund(it.id)} className="text-gray-300 hover:text-red-500 p-0.5" title="Remove">
                <Trash2 size={12} />
              </button>
            </div>
          );
        }
        // edit
        return (
          <div key={`e-${it.id}`} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200 text-xs" data-testid="history-row-edit">
            <span className="w-7 h-7 rounded-full bg-gray-200 text-gray-700 flex items-center justify-center flex-shrink-0">
              <Edit2 size={12} />
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-900 truncate">
                Expected changed: {inr(it.old)} → {inr(it.new)}
                <span className={`ml-1.5 text-[10px] font-bold ${it.delta >= 0 ? 'text-green-700' : 'text-rose-700'}`}>
                  ({it.delta >= 0 ? '+' : ''}{inr(Math.abs(it.delta))})
                </span>
              </p>
              <p className="text-[10px] text-gray-500 truncate">{fmtDate(it.at)} · {it.reason}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default DepositCard;
